import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { GBP } from "@/lib/helpers";
import { useFeatures } from "@/lib/useFeatures";
import { FeatureLocked } from "@/components/Common";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Banknote, ArrowUpCircle, ArrowDownCircle, Lock } from "lucide-react";
import { toast } from "sonner";

export default function CashDrawer() {
  const { rid } = useParams();
  const features = useFeatures(rid);
  const [current, setCurrent] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [openFloat, setOpenFloat] = useState("100");
  const [mv, setMv] = useState({ type: "IN", amount: "", reason: "" });
  const [counted, setCounted] = useState("");
  const [notes, setNotes] = useState("");

  const load = useCallback(() => {
    api.get(`/restaurants/${rid}/cash/current`).then((r) => setCurrent(r.data)).catch(() => {});
    api.get(`/restaurants/${rid}/cash/shifts`).then((r) => setShifts(r.data)).catch(() => {});
  }, [rid]);
  useEffect(() => { load(); }, [load]);

  if (features && !features.CASH_DRAWER) return <FeatureLocked label="Cash Drawer" />;

  const openDrawer = async () => {
    try { await api.post(`/restaurants/${rid}/cash/open`, { opening_float: parseFloat(openFloat) || 0 }); toast.success("Drawer opened"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not open drawer"); }
  };
  const addMovement = async () => {
    if (!mv.amount) return toast.error("Enter an amount");
    await api.post(`/restaurants/${rid}/cash/movement`, { type: mv.type, amount: parseFloat(mv.amount), reason: mv.reason });
    toast.success("Movement recorded"); setMv({ type: "IN", amount: "", reason: "" }); load();
  };
  const closeDrawer = async () => {
    if (counted === "") return toast.error("Enter the counted cash");
    const res = await api.post(`/restaurants/${rid}/cash/close`, { counted_cash: parseFloat(counted), notes });
    const v = res.data.variance;
    toast[v === 0 ? "success" : "warning"](`Drawer closed · variance ${GBP(v)}`);
    setCounted(""); setNotes(""); load();
  };

  const s = current?.shift;
  return (
    <div>
      <PageHeader title="Cash drawer" subtitle="Open a shift with a float, track cash in/out and cash sales, then reconcile on close." />

      {!current?.open ? (
        <Card className="p-6 rounded-2xl max-w-md" data-testid="cash-open-panel">
          <div className="flex items-center gap-2 mb-3"><Lock className="h-5 w-5 text-muted-foreground" /><p className="font-display font-semibold">Drawer is closed</p></div>
          <Label>Opening float (£)</Label>
          <Input type="number" step="1" className="h-11 mt-1.5" value={openFloat} onChange={(e) => setOpenFloat(e.target.value)} data-testid="opening-float-input" />
          <Button className="h-11 mt-4 w-full" onClick={openDrawer} data-testid="open-drawer-btn"><Banknote className="h-4 w-4 mr-2" /> Open drawer</Button>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-start">
          <div className="space-y-4">
            <Card className="p-6 rounded-2xl" data-testid="cash-summary">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-muted-foreground">Opening float</p><p className="font-display text-xl font-bold">{GBP(s.opening_float)}</p></div>
                <div><p className="text-sm text-muted-foreground">Expected in drawer</p><p className="font-display text-2xl font-bold text-primary" data-testid="expected-cash">{GBP(current.expected_cash)}</p></div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Opened by {s.opened_by} · {new Date(s.opened_at).toLocaleString()}</p>
            </Card>

            <Card className="p-6 rounded-2xl">
              <p className="font-display font-semibold mb-3">Movements</p>
              <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="movements-list">
                {current.movements.length === 0 && <p className="text-sm text-muted-foreground">No movements yet.</p>}
                {current.movements.map((m) => (
                  <div key={m.id} className="flex items-center justify-between rounded-lg border border-border p-2.5">
                    <div className="flex items-center gap-2">
                      {m.type === "OUT" ? <ArrowDownCircle className="h-4 w-4 text-red-500" /> : <ArrowUpCircle className="h-4 w-4 text-emerald-600" />}
                      <div><p className="text-sm font-medium">{m.reason || m.type}</p><p className="text-xs text-muted-foreground">{m.type} · {m.by_user}</p></div>
                    </div>
                    <span className={`font-medium ${m.type === "OUT" ? "text-red-600" : "text-emerald-600"}`}>{m.type === "OUT" ? "−" : "+"}{GBP(m.amount)}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <div className="space-y-4">
            <Card className="p-5 rounded-2xl">
              <p className="font-display font-semibold mb-3">Record movement</p>
              <div className="flex gap-2 mb-2">
                <Button variant={mv.type === "IN" ? "default" : "outline"} className="flex-1 h-10" data-testid="mv-type-IN" onClick={() => setMv({ ...mv, type: "IN" })}>Cash in</Button>
                <Button variant={mv.type === "OUT" ? "default" : "outline"} className="flex-1 h-10" data-testid="mv-type-OUT" onClick={() => setMv({ ...mv, type: "OUT" })}>Cash out</Button>
              </div>
              <Input type="number" step="0.01" placeholder="Amount £" className="h-11 mb-2" value={mv.amount} onChange={(e) => setMv({ ...mv, amount: e.target.value })} data-testid="mv-amount" />
              <Input placeholder="Reason (e.g. petty cash, tip)" className="h-11 mb-3" value={mv.reason} onChange={(e) => setMv({ ...mv, reason: e.target.value })} data-testid="mv-reason" />
              <Button className="h-11 w-full" onClick={addMovement} data-testid="add-movement-btn">Add movement</Button>
            </Card>

            <Card className="p-5 rounded-2xl border-2 border-amber-300/50">
              <p className="font-display font-semibold mb-3">Close & reconcile</p>
              <Label>Counted cash (£)</Label>
              <Input type="number" step="0.01" className="h-11 mt-1.5 mb-2" value={counted} onChange={(e) => setCounted(e.target.value)} data-testid="counted-cash-input" />
              {counted !== "" && (
                <p className="text-sm mb-2" data-testid="variance-preview">Variance: <b className={Math.round((parseFloat(counted) - current.expected_cash) * 100) === 0 ? "text-emerald-600" : "text-red-600"}>{GBP(Math.round((parseFloat(counted) - current.expected_cash) * 100) / 100)}</b></p>
              )}
              <Input placeholder="Notes (optional)" className="h-11 mb-3" value={notes} onChange={(e) => setNotes(e.target.value)} />
              <Button variant="destructive" className="h-11 w-full" onClick={closeDrawer} data-testid="close-drawer-btn">Close drawer</Button>
            </Card>
          </div>
        </div>
      )}

      {shifts.length > 0 && (
        <div className="mt-8">
          <h3 className="font-display font-bold text-lg mb-3">Past shifts</h3>
          <div className="space-y-2">
            {shifts.map((sh) => (
              <Card key={sh.id} data-testid={`shift-${sh.id}`} className="p-4 rounded-xl flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-medium">{new Date(sh.opened_at).toLocaleString()} → {sh.closed_at ? new Date(sh.closed_at).toLocaleTimeString() : "—"}</p>
                  <p className="text-sm text-muted-foreground">Float {GBP(sh.opening_float)} · Expected {GBP(sh.expected_cash)} · Counted {GBP(sh.counted_cash)} · by {sh.closed_by}</p>
                </div>
                <Badge variant="outline" className={sh.variance === 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}>
                  Variance {GBP(sh.variance)}
                </Badge>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
